import React from 'react';
import { Alert, App, Input, Space, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { formatUnit } from '../../utils/units';
import { formatDateEuropean } from '../../utils/dateUtils';
import { formatLocalizedQuantity } from '../../utils/numberFormatting';
import { CARGO_QUANTITY_TOLERANCE } from '../../utils/transportCargoAllocation';

type ModalApi = ReturnType<typeof App.useApp>['modal'];

export interface OperationalWarning {
  code: string;
  details?: Record<string, any>;
}

function readConfirmation(error: any) {
  const data = error?.response?.data;
  if (data?.code !== 'OPERATIONAL_EXCEPTION_CONFIRMATION_REQUIRED') return null;
  if (!data.confirmationFingerprint || !Array.isArray(data.warnings)) return null;
  return data as {
    confirmationFingerprint: string;
    warnings: OperationalWarning[];
    reasonMinLength?: number;
    reasonMaxLength?: number;
  };
}

const quantity = (value: unknown) => formatLocalizedQuantity(Number(value) || 0, 'pl');

/**
 * Consistency warnings (decision 2026-09-18): quantities, purchase prices
 * and document numbers only — never customer sale prices or margin.
 */
function consistencyWarningText(warning: OperationalWarning, t: TFunction): string | null {
  const details = warning.details || {};
  switch (warning.code) {
    case 'WZ_QUANTITY_DIFFERS_FROM_ORDER':
      return String(t('common:operationalException.warnings.wzQuantityDiffersFromOrder', {
        ...details,
        unit: formatUnit(details.unit, 'pl'),
        wzQuantity: quantity(details.wzQuantity),
        alreadyReleasedQuantity: quantity(details.alreadyReleasedQuantity),
        issuedQuantity: quantity(details.issuedQuantity),
        orderQuantity: quantity(details.orderQuantity),
        difference: quantity(details.difference),
      }));
    case 'WZ_VARIANT_DIFFERS_FROM_ORDER':
      return String(t('common:operationalException.warnings.wzVariantDiffersFromOrder', details));
    case 'ZD_QUANTITY_DIFFERS_FROM_LINKED_ORDERS':
      return String(t('common:operationalException.warnings.zdQuantityDiffersFromLinkedOrders', {
        ...details,
        unit: formatUnit(details.unit, 'pl'),
        orderNumbers: Array.isArray(details.orderNumbers) ? details.orderNumbers.join(', ') : '',
        supplierOrderQuantity: quantity(details.supplierOrderQuantity),
        linkedOrdersQuantity: quantity(details.linkedOrdersQuantity),
        difference: quantity(details.difference),
      }));
    case 'PURCHASE_PRICE_DEVIATES':
      return String(t('common:operationalException.warnings.purchasePriceDeviates', {
        ...details,
        pricingUnit: formatUnit(details.pricingUnit, 'pl'),
        unitCost: quantity(details.unitCost),
        medianUnitCost: quantity(details.medianUnitCost),
        deviationPercent: quantity(details.deviationPercent),
        thresholdPercent: quantity(details.thresholdPercent),
      }));
    case 'SUPPLIER_ORDER_VALUATION_AFFECTS_LEGACY_MARGIN': {
      // Owner decision 2026-09-21: order numbers and the consequence only —
      // OPERATIONS and ADMIN read exactly the same sentence, with no amounts.
      const orderNumbers = Array.isArray(details.orderNumbers) ? details.orderNumbers : [];
      return String(t('common:operationalException.warnings.supplierOrderValuationAffectsLegacyMargin', {
        count: Number(details.orderCount) || orderNumbers.length,
        orderNumbers: orderNumbers.join(', ') || '—',
      }));
    }
    case 'DELIVERED_WITHOUT_RELEASED_WZ': {
      const wzNumbers = Array.isArray(details.wzNumbers) ? details.wzNumbers : [];
      return String(t('common:operationalException.warnings.deliveredWithoutReleasedWZ', {
        orderNumber: details.orderNumber || '',
        wzNumbersSuffix: wzNumbers.length > 0
          ? t('common:operationalException.warnings.deliveredWithoutReleasedWZDocuments', { wzNumbers: wzNumbers.join(', ') })
          : '',
      }));
    }
    default:
      return null;
  }
}

export function warningText(warning: OperationalWarning, t: TFunction) {
  const details = warning.details || {};
  const consistency = consistencyWarningText(warning, t);
  if (consistency !== null) return consistency;
  if (warning.code === 'WZ_MISSING_PURCHASE_INVOICE' || warning.code === 'WZ_BEFORE_PURCHASE_INVOICE' || warning.code === 'WZ_PENDING_STOCK_ISSUE') {
    return String(t('common:operationalException.warnings.wzBeforePurchaseInvoice', { ...details, unit: formatUnit(details.unit, 'pl') }));
  }
  if (warning.code === 'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED') {
    return String(t('common:operationalException.warnings.pzOverReceipt', {
      ...details,
      unit: formatUnit(details.unit, 'pl'),
    }));
  }
  if (warning.code === 'PZ_INVOICE_DATE_AFTER_RECEIPT') {
    return String(t('common:operationalException.warnings.pzInvoiceDateAfterReceipt', details));
  }
  if (warning.code === 'PZ_INVOICE_NET_AMOUNT_MISMATCH') {
    return String(t('common:operationalException.warnings.pzInvoiceNetAmountMismatch', details));
  }
  if (warning.code === 'CARGO_OVER_ALLOCATION') {
    const rows = Array.isArray(details.overflow) ? details.overflow : [];
    return rows.map((row: Record<string, unknown>) => {
      // `allowed` is intentionally the cumulative guard ceiling: it includes
      // the 0.5-unit tolerance so repeated transports cannot each consume a
      // fresh tolerance. It must never be labelled as available stock.
      const ordered = Number(row.ordered) || 0;
      const allocatedElsewhere = Number(row.allocatedElsewhere) || 0;
      const available = Math.max(ordered - allocatedElsewhere, 0);
      const formatQuantity = (value: number) => formatLocalizedQuantity(value, 'pl');
      return String(t('common:operationalException.warnings.cargoOverAllocation', {
        ...row,
        attempted: formatQuantity(Number(row.attempted) || 0),
        ordered: formatQuantity(ordered),
        allocatedElsewhere: formatQuantity(allocatedElsewhere),
        available: formatQuantity(available),
        tolerance: formatQuantity(CARGO_QUANTITY_TOLERANCE),
      }));
    }).join('\n');
  }
  if (warning.code === 'TRANSPORT_DELIVERY_EXCEEDS_PLANNED') {
    return String(t('common:operationalException.warnings.transportDeliveryExceedsPlanned', {
      ...details,
      unit: formatUnit(details.unit, 'pl'),
      plannedQuantity: formatLocalizedQuantity(Number(details.plannedQuantity) || 0, 'pl'),
      deliveredQuantity: formatLocalizedQuantity(Number(details.deliveredQuantity) || 0, 'pl'),
      difference: formatLocalizedQuantity(Number(details.difference) || 0, 'pl'),
    }));
  }
  if (warning.code === 'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING') {
    return String(t('common:operationalException.warnings.transportDeliveryBeforeLoading', {
      actualDelivery: formatDateEuropean(details.actualDelivery),
      loadingDate: formatDateEuropean(details.loadingDate),
    }));
  }
  return warning.code;
}

async function askForReason(
  modal: ModalApi,
  warnings: OperationalWarning[],
  maxLength: number,
  t: TFunction,
  minLength = 1,
): Promise<string | null> {
  let reason = '';
  return new Promise((resolve) => {
    modal.confirm({
      title: t('common:operationalException.title'),
      width: 620,
      okText: t('common:operationalException.continue'),
      cancelText: t('common:cancel'),
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('common:operationalException.description')}
          />
          {warnings.map((warning, index) => (
            <Typography.Paragraph key={`${warning.code}-${index}`} style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
              {warningText(warning, t)}
            </Typography.Paragraph>
          ))}
          <div>
            <Typography.Text strong>{t('common:operationalException.reasonLabel')}</Typography.Text>
            <Input.TextArea
              rows={3}
              maxLength={maxLength}
              showCount
              placeholder={t('common:operationalException.reasonPlaceholder')}
              onChange={(event) => { reason = event.target.value; }}
            />
            {minLength > 1 && (
              <Typography.Text type="secondary">
                {t('common:operationalException.reasonMinLength', { min: minLength })}
              </Typography.Text>
            )}
          </div>
        </Space>
      ),
      onOk: () => {
        if (!reason.trim()) return Promise.reject(new Error(t('common:operationalException.reasonRequired')));
        if (reason.trim().length < minLength) {
          return Promise.reject(new Error(t('common:operationalException.reasonMinLength', { min: minLength })));
        }
        resolve(reason.trim());
        return Promise.resolve();
      },
      onCancel: () => resolve(null),
    });
  });
}

export async function submitWithOperationalException<T>(
  request: (payload: Record<string, unknown>) => Promise<T>,
  payload: Record<string, unknown>,
  modal: ModalApi,
  t: TFunction,
): Promise<T | null> {
  let nextPayload = payload;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await request(nextPayload);
    } catch (error) {
      const confirmation = readConfirmation(error);
      if (!confirmation) throw error;
      const reason = await askForReason(
        modal,
        confirmation.warnings,
        confirmation.reasonMaxLength || 500,
        t,
        confirmation.reasonMinLength || 1,
      );
      if (!reason) return null;
      nextPayload = {
        ...payload,
        confirmOperationalException: true,
        operationalExceptionFingerprint: confirmation.confirmationFingerprint,
        operationalExceptionReason: reason,
      };
    }
  }
  throw new Error('Operational exception confirmation changed repeatedly');
}
